import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  OnInit,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { assert } from '@sama/js-core';
import { DirectiveSuperclass } from '@sama/ng-core';
import { findIndex, matches } from 'lodash';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { WorkspaceLoadingService } from '../../../shared/misc-services/workspace-loading.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { FinalizeSampleDialogComponent } from '../finalize-sample-dialog/finalize-sample-dialog.component';
import { SampleTaskNavigationService } from '../services/sample-task-navigation.service';
import { TaskReviewFinalizeResourceService } from '../services/task-review-finalize-resource.service';
import { SamplingWorkspaceStore } from '../state/sampling-workspace-store';

@Component({
  selector: 'h3-finalize-button',
  templateUrl: './finalize-button.component.html',
  styleUrl: './finalize-button.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatButtonModule, MatMenuModule],
})
export class FinalizeButtonComponent
  extends DirectiveSuperclass
  implements OnInit
{
  private readonly store = inject(SamplingWorkspaceStore);
  private readonly taskStore = inject(TaskWorkspaceStore)('task');
  private readonly taskId$ = this.taskStore('id').$;
  private readonly navigationService = inject(SampleTaskNavigationService);
  private readonly sampleTaskFinalizeResource = inject(
    TaskReviewFinalizeResourceService,
  );
  private readonly matDialog = inject(MatDialog);
  private readonly injector = inject(Injector);
  private readonly workspaceLoadingService = inject(WorkspaceLoadingService);

  hasFinalized$ = this.store.hasSampleFinalized$();
  isReviewed$ = combineLatest([this.taskId$, this.store('sampleTasks').$]).pipe(
    map(
      ([taskId, sampleTasks]) =>
        sampleTasks.find((task) => task.task_id === taskId)?.reviewed,
    ),
  );
  isLastUnreviewed$ = new BehaviorSubject(false);

  ngOnInit() {
    this.subscribeTo(
      combineLatest([this.isReviewed$, this.store('sampleTasks').$]).pipe(
        map(([isReviewed, sampleTasks]) => {
          const unreivewed = sampleTasks.filter((task) => !task.reviewed);
          return !isReviewed && unreivewed.length === 1;
        }),
      ),
      (isLastUnreviewed) => {
        this.isLastUnreviewed$.next(isLastUnreviewed);
      },
    );
  }

  async markAsReviewed() {
    const sampleTask = this.store
      .state()
      .sampleTasks.find(matches({ task_id: this.taskStore.state().id }));

    assert(sampleTask, 'Sample task must be found in sample tasks');

    this.workspaceLoadingService.isLoading$.next(true);
    await this.sampleTaskFinalizeResource
      .markAsReviewed(sampleTask.id)
      .finally(() => this.workspaceLoadingService.isLoading$.next(false));
    const nextUnreviewedTaskIndex = this.findNextUnreviewedTask();
    this.navigationService.navigateToTask(
      nextUnreviewedTaskIndex + 1, // position is 1 based
      'mark_as_reviewed_button',
    );
  }

  openMenu(event: MouseEvent) {
    // so that mark as reviewed button doesn't get clicked
    event.stopPropagation();
  }

  finalizeSample() {
    this.matDialog.open(FinalizeSampleDialogComponent, {
      width: '527px',
      height: '316px',
      injector: this.injector,
      backdropClass: 'dialog-backdrop',
      data: {
        isLastUnreviewed: this.isLastUnreviewed$.value,
      },
    });
  }

  private findNextUnreviewedTask() {
    const sampleTasks = this.store.state().sampleTasks;
    const taskId = this.taskStore.state().id;
    const currentTaskIndex = sampleTasks.findIndex(
      ({ task_id }) => task_id === taskId,
    );

    const unreviewedTaskIndex = findIndex(
      sampleTasks,
      (task) => !task.reviewed,
      currentTaskIndex + 1,
    );

    if (unreviewedTaskIndex !== -1) {
      return unreviewedTaskIndex;
    }

    return findIndex(
      sampleTasks,
      (task) => !task.reviewed && task.task_id !== taskId,
    );
  }
}
